import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Instagram, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/data";

const API = "/.netlify/functions";
const initial = { name: "", email: "", message: "", consent: false };

export default function Contacto() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Diga-nos o seu nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email inválido.";
    if (form.message.trim().length < 5) e.message = "Escreva uma mensagem.";
    if (!form.consent) e.consent = "É necessário aceitar a Política de Privacidade.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Mensagem enviada!", {
        description: "Recebemos o seu contacto.",
      });
      setForm(initial);
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full border-0 border-b border-ink/15 bg-transparent px-0 py-3 text-ink placeholder:text-muted-foreground/60 outline-none transition-colors duration-300 focus:border-regatta";

  return (
    <>
      <section className="relative overflow-hidden pt-36 md:pt-44">
        <div className="pointer-events-none absolute left-[-8%] top-6 h-[380px] w-[380px] rounded-full bg-lightblue opacity-20 blur-[130px]" />
        <div className="mx-auto max-w-[1440px] px-6 pb-12 md:px-12">
          <Reveal>
            <span className="text-xs font-medium uppercase tracking-[0.28em] text-grad">Contacto</span>
            <h1 className="mt-5 max-w-4xl font-display text-5xl font-extrabold leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-7xl text-balance">
              Vamos dar visibilidade ao seu negócio.
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Conte-nos o que precisa. Respondemos o mais breve possível, com uma proposta clara e
              sem compromisso.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 md:px-12 md:pb-32">
        <div className="grid gap-14 lg:grid-cols-[1fr_0.85fr] lg:gap-20">
          <Reveal>
            <form onSubmit={handleSubmit} data-testid="contact-form" noValidate className="flex flex-col gap-8">
              <div>
                <label htmlFor="name" className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Nome</label>
                <input id="name" data-testid="contact-name" className={field} placeholder="O seu nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                {errors.name && <p className="mt-2 text-xs text-red-600" data-testid="error-name">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="email" className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                <input id="email" type="email" data-testid="contact-email" className={field} placeholder="email@exemplo.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                {errors.email && <p className="mt-2 text-xs text-red-600" data-testid="error-email">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="message" className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Mensagem</label>
                <textarea id="message" data-testid="contact-message" rows={4} className={`${field} resize-none`} placeholder="Conte-nos sobre o seu negócio e o que precisa..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                {errors.message && <p className="mt-2 text-xs text-red-600" data-testid="error-message">{errors.message}</p>}
              </div>
              <div>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
                  <input type="checkbox" data-testid="contact-consent" className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-regatta" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
                  <span>
                    Autorizo o tratamento dos meus dados de acordo com a{" "}
                    <Link to="/politica-de-privacidade" className="text-regatta underline underline-offset-2">Política de Privacidade</Link> (RGPD).
                  </span>
                </label>
                {errors.consent && <p className="mt-2 text-xs text-red-600" data-testid="error-consent">{errors.consent}</p>}
              </div>
              <button type="submit" data-testid="contact-submit" disabled={loading}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-grad px-8 py-4 text-base font-medium text-white shadow-[0_16px_40px_-14px_rgba(79,111,214,0.7)] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto">
                {loading ? (<><Loader2 className="h-5 w-5 animate-spin" /> A enviar...</>) : (<>Enviar mensagem<ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>)}
              </button>
            </form>
          </Reveal>

          <Reveal delay={1}>
            <div className="noise relative overflow-hidden rounded-[1.6rem] bg-ink p-8 text-white md:p-10">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-grad opacity-40 blur-3xl" />
              <p className="relative text-xs uppercase tracking-[0.25em] text-white/50">Fale connosco</p>
              <div className="relative mt-8 flex flex-col gap-7">
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-brightblue transition-colors group-hover:bg-grad group-hover:text-white"><Phone className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-white/50">Telefone</span><span className="text-lg">{CONTACT.phone}</span></span>
                </a>
                <a href={CONTACT.emailHref} data-testid="contact-email-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-brightblue transition-colors group-hover:bg-grad group-hover:text-white"><Mail className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-white/50">Email</span><span className="break-all text-lg">{CONTACT.email}</span></span>
                </a>
                <a href={CONTACT.instagramHref} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-brightblue transition-colors group-hover:bg-grad group-hover:text-white"><Instagram className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-white/50">Instagram</span><span className="text-lg">{CONTACT.instagram}</span></span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-brightblue"><MapPin className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-white/50">Localização</span><span className="text-lg">{CONTACT.location}</span></span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
